import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { GraphQLClient } from "../../../../shared/gql-client";
import { toast } from "sonner";
import i18next from "i18next";
import InvoiceService from "../service/invoiceService";
import {
  InvoiceRow,
  InvoiceFilters,
} from "../../modules/features/InvoiceTable";

const invoiceApi = new InvoiceService();

export interface InvoiceDataState {
  loading: boolean;
  data: InvoiceRow[];
  filters: InvoiceFilters;
  filtersInitialized: boolean;
}

export const getInvoiceData = createAsyncThunk(
  "invoice/get/invoiceData",
  async (args: Partial<InvoiceFilters> = {}, thunkAPI) => {
    const { client } = thunkAPI.extra as { client: GraphQLClient };
    try {
      invoiceApi.setClient(client);
      return await invoiceApi.queryInvoiceData(args);
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const exportInvoiceData = createAsyncThunk(
  "invoice/export/invoiceDataExport",
  async (
    args: {
      status: string;
      relationId?: string;
      invoice: boolean;
      inOutStorageFrom?: string;
      inOutStorageUntil?: string;
      pickupDeliveryDateFrom?: string;
      pickupDeliveryDateUntil?: string;
    },
    thunkAPI,
  ) => {
    const { client } = thunkAPI.extra as { client: GraphQLClient };
    try {
      invoiceApi.setClient(client);
      return await invoiceApi.exportInvoiceData(args);
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const invoiceDataSlice = createSlice({
  name: "invoiceData",
  initialState: {
    loading: true,
    data: [],
    filtersInitialized: false,
    filters: {
      relationId: "",
      status: "to_invoice",
      InStorageFrom: "",
      InStorageUntil: "",
    },
  } as InvoiceDataState,
  reducers: {
    setInvoiceFilters(state, action) {
      state.filters = { ...state.filters, ...action.payload };
      state.filtersInitialized = true;
    },
    resetInvoiceFilters(state) {
      state.filters = {
        relationId: "",
        status: "to_invoice",
        InStorageFrom: "",
        InStorageUntil: "",
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getInvoiceData.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getInvoiceData.fulfilled, (state, action) => {
      state.loading = false;
      state.data = Array.isArray(action.payload)
        ? action.payload
        : action.payload
          ? [action.payload]
          : [];
    });
    builder.addCase(getInvoiceData.rejected, (state, action) => {
      state.loading = false;
      toast.error(i18next?.t("common:Error loading invoice data"));
      console.error(action.payload);
    });
  },
});

export const { setInvoiceFilters, resetInvoiceFilters } =
  invoiceDataSlice.actions;
export default invoiceDataSlice.reducer;
